// src/pages/FrogGenusPage.js
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getProductsByGenus } from '../services/api';
import ProductCard from '../components/Products/ProductCard';
import SpeciesLearnGrid from '../components/Products/SpeciesLearnGrid';
import EmptyStateWayOn from '../components/common/EmptyStateWayOn';
import { Row, Col, Container } from 'react-bootstrap';
import SEO from '../components/SEO';
import { getGenusKeywords } from '../constants/seoKeywords';
import Breadcrumbs from '../components/Navigation/Breadcrumbs';
import SkeletonCardGrid from '../components/common/SkeletonCardGrid';
import '../styles/products.css';
import '../styles/ff-empty-state.css';

// S3-01.3 follow-up (2026-08-30): parametrized so /geckos/:genus reuses this
// page (genus is naturally type-scoped — no genus spans frogs and geckos).
const FrogGenusPage = ({ kind = 'frogs' }) => {
  const { genus } = useParams();
  const kindLabel = kind === 'geckos' ? 'Geckos' : 'Frogs';
  const basePath = kind === 'geckos' ? '/geckos' : '/frogs';
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  // Distinguish "no products in genus" from "load failed" — 2026-07-02
  // fix identical to FrogsPage / GeckosPage / PlantsPage. Silent
  // try/catch was showing "No frogs from this genus available right
  // now" on transient backend errors.
  const [loadError, setLoadError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      // Type-scoped (operator QA 2026-08-30): the QADELETE fixture
      // genus spans frog+gecko+plant and rendered all three here.
      const productsResponse = await getProductsByGenus(genus, kind === 'geckos' ? 3 : 0);
      const sorted = productsResponse.sort((a, b) => {
        const aAvailable = a.is_for_sale && a.count > 0;
        const bAvailable = b.is_for_sale && b.count > 0;
        return bAvailable - aAvailable;
      });
      setProducts(sorted);
    } catch (error) {
      console.error('[FrogGenusPage] load failed:', error?.message || error);
      setLoadError(
        error?.code === 'ECONNABORTED' || /timeout/i.test(error?.message || '')
          ? 'Having trouble reaching our inventory just now. Please try again in a moment.'
          : 'Something went wrong loading this genus. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [genus, kind]);  // kind: /frogs/X <-> /geckos/X is a props update, not a remount

  const genusCapitalized = genus.charAt(0).toUpperCase() + genus.slice(1);

  // Determine the correct frog type for SEO
  const getFrogType = () => {
    const genusLower = genus.toLowerCase();

    // Dart frogs (poison dart frogs)
    if (['dendrobates', 'phyllobates', 'ranitomeya', 'oophaga', 'epipedobates', 'ameerega'].includes(genusLower)) {
      return 'Dart Frogs';
    }

    // Tree frogs
    if (['agalychnis', 'cruziohyla', 'trachycephalus', 'litoria', 'theloderma', 'nyctixalus'].includes(genusLower)) {
      return 'Tree Frogs';
    }

    // Toads
    if (['bombina', 'atelopus', 'melanophryniscus'].includes(genusLower)) {
      return 'Toads';
    }

    // Pac-Man Frogs
    if (['ceratophrys'].includes(genusLower)) {
      return 'Pac-Man Frogs';
    }

    // Gecko genera (used via the /geckos/:genus route)
    if (['rhacodactylus', 'eublepharis', 'correlophus', 'mniarogekko'].includes(genusLower)) {
      return 'Geckos';
    }

    // Default fallback
    return kindLabel;
  };

  const frogType = getFrogType();
  const frogTypeLower = frogType.toLowerCase();

  // Get genus-specific keywords from centralized constants
  const genusKeywords = getGenusKeywords(genus);

  return (
    <>
      <SEO
        title={`${genusCapitalized} ${frogType} for Sale | Feeling Froggy`}
        description={`${genusCapitalized} ${frogTypeLower} for sale - captive-bred with live arrival guarantee. Browse available morphs, read care guides & shop supplies.`}
        canonical={`https://feelingfroggy.llc${basePath}/${genus}`}
        keywords={genusKeywords}
      />
      <Container className="products-page">
        <Breadcrumbs items={[
          { name: "Home", url: "/" },
          { name: kindLabel, url: basePath },
          { name: genusCapitalized, url: `${basePath}/${genus.toLowerCase()}` }
        ]} />

        <h1 className="ff-heading-h1">{genusCapitalized}</h1>

        {loading ? (
          <SkeletonCardGrid count={8} />
        ) : loadError ? (
          <div className="text-center py-5">
            <p className="text-muted mb-3">{loadError}</p>
            <button
              type="button"
              className="ff-btn ff-btn--secondary"
              onClick={fetchData}
            >
              Try again
            </button>
          </div>
        ) : products.length === 0 ? (
          /* #103 zero-inventory graceful degradation — LEARN mode, genus-
             scoped (the public species list filters genus__iexact, so the
             lowercase route param matches). Same fallback-to-old-empty-
             state behavior as FrogsPage. */
          <SpeciesLearnGrid
            genus={genus}
            // I-03 close 1c: the learn grid is scoped to this page's type
            // like the products fetch above, so a species of another type
            // (an "Other reptile" sharing a genus word) never lands on a
            // frog or gecko genus page with a "Notify me".
            speciesType={kind === 'geckos' ? 3 : 0}
            fallback={(
              <div className="text-center">
                <p>{`No ${kindLabel.toLowerCase()} from this genus available right now.`}</p>
                <EmptyStateWayOn />
              </div>
            )}
          />
        ) : (
          <Row className="g-4">
            {products.map((product, productIdx) => (
              <Col key={product.id} xs={12} sm={6} md={4}>
                <ProductCard product={product} eagerImage={productIdx < 6} />
              </Col>
            ))}
          </Row>
        )}
      </Container>
    </>
  );
};

export default FrogGenusPage;